import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { BaseFindDto, FindResponse, UpdateResponse } from "@remar/shared/dist/api/baseApiService";
import { CreateUpdateLiveStream, LessonComment, LiveStream } from "@remar/shared/dist/models";

import { filterLiveStreamChatMessages } from "@remar/shared/dist/utils/filterLiveStreamChatMessages";
import { performFileUpload } from "@remar/shared/dist/utils/serviceUtils/uploaders";

import { GLOBAL_CONSTANTS } from "../../../core/constants";
import { RootState } from "../../index";
import { liveStreamService } from "../../services/liveStream/liveStream.service";

interface LiveStreamState {
	isCreatingStream: boolean;
	mainImageKey: string | null;
	isFetchingStream: boolean;
	isFetchingHistory: boolean;
	streamHistory: FindResponse<LiveStream> | null;
	selectedLiveStreamViewersCount: number;
	selectedLiveStream: LiveStream | null;
	streamComments: FindResponse<LessonComment> | null;
	isFetchingComments: boolean;
}

const initialState: LiveStreamState = {
	isCreatingStream: false,
	mainImageKey: null,
	isFetchingStream: false,
	isFetchingHistory: false,
	streamHistory: null,
	selectedLiveStreamViewersCount: 0,
	selectedLiveStream: null,
	streamComments: null,
	isFetchingComments: false
};

export const createLiveStream = createAsyncThunk(
	"liveStream/createLiveStream",
	async ({ data, cb }: { data: CreateUpdateLiveStream; cb: (res: LiveStream) => void }, { rejectWithValue }) => {
		try {
			const { id, ...rest } = data;
			const res = !!id
				? await liveStreamService.update({ filters: { id }, data: rest })
				: await liveStreamService.create(rest);
			cb && cb(res);
			return res;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const updateLiveStream = createAsyncThunk(
	"liveStream/updateLiveStream",
	async (
		{ id, data, cb }: { id: string; data: CreateUpdateLiveStream; cb: (res: UpdateResponse<LiveStream>) => void },
		{ rejectWithValue }
	) => {
		try {
			const res = await liveStreamService.update({ filters: { id }, data });
			cb && cb(res);
			return res;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const deleteLiveStream = createAsyncThunk(
	"liveStream/deleteLiveStream",
	async (id: string, { rejectWithValue, dispatch }) => {
		try {
			await liveStreamService.delete({ filters: { id } });
			dispatch(fetchLiveStreamHistory({ include: ["allowedLocations"], orderBy: { createdAt: "DESC" } }));
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const fetchLiveStream = createAsyncThunk(
	"liveStream/fetchLiveStream",
	async ({ id }: { id: number }, { rejectWithValue }) => {
		return liveStreamService.findOne(id).catch(e => rejectWithValue(e));
	}
);

export const fetchLiveStreamHistory = createAsyncThunk(
	"liveStream/fetchLiveStreamHistory",
	async (options: BaseFindDto, { rejectWithValue }) => {
		return liveStreamService.find(options).catch(e => rejectWithValue(e));
	}
);

export const fetchLiveStreamComments = createAsyncThunk(
	"liveStream/fetchLiveStreamComments",
	async ({ streamId, page = 1 }: { streamId: string; page: number }, { rejectWithValue }) => {
		return liveStreamService
			.fetchComments({ streamId, options: { page, include: ["user"], orderBy: { createdAt: "DESC" } } })
			.catch(e => rejectWithValue(e));
	}
);

export const deleteLiveStreamComment = createAsyncThunk(
	"liveStream/deleteLiveStreamComment",
	async ({ id, commentId }: { id: string; commentId: string }, { rejectWithValue }) => {
		try {
			return await liveStreamService.deleteComment({ streamId: id, commentId });
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const fetchLiveStreamViewersCount = createAsyncThunk(
	"liveStream/fetchLiveStreamViewersCount",
	async ({ streamId }: { streamId: string }): Promise<{ totalWebRTCWatchersCount: number }> => {
		const res = await fetch(`${GLOBAL_CONSTANTS.ANT_MEDIA_SERVER_STATISTICS_URL}/${streamId}/broadcast-statistics`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json"
			}
		});
		return res.json();
	}
);

export const uploadAttachment = createAsyncThunk(
	"liveStream/uploadAttachment",
	async (data: {
		file: Partial<File>;
		options: {
			onError: () => void;
			onProgress: ({}: { loaded: number; total: number }) => void;
			onUploaded: () => void;
		};
	}) => {
		const { file, options } = data;
		const { key } = await performFileUpload({ file }, options);
		return key;
	}
);

export const postLiveStreamComment = createAsyncThunk(
	"liveStream/postLiveStreamComment",
	async ({ comment, streamId }: { comment: string; streamId: string }, { rejectWithValue }) => {
		try {
			return await liveStreamService.postComment({ comment, streamId });
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const liveStreamSlice = createSlice({
	name: "liveStream",
	initialState,
	reducers: {
		AddNewComment: (state, action) => {
			if (state.streamComments) {
				state.streamComments.items = filterLiveStreamChatMessages(state.streamComments?.items, action.payload);
			} else {
				state.streamComments = { items: [action.payload] } as FindResponse<LessonComment>;
			}
		},
		clearSelectedStream: state => {
			state.selectedLiveStream = null;
			state.mainImageKey = null;
		}
	},
	extraReducers: builder =>
		builder
			.addCase(createLiveStream.pending, state => {
				state.isCreatingStream = true;
			})
			.addCase(createLiveStream.fulfilled, state => {
				state.isCreatingStream = false;
			})
			.addCase(createLiveStream.rejected, state => {
				state.isCreatingStream = false;
			})
			.addCase(fetchLiveStreamHistory.pending, state => {
				state.isFetchingHistory = true;
			})
			.addCase(fetchLiveStreamHistory.fulfilled, (state, action) => {
				state.isFetchingHistory = false;
				state.streamHistory = action.payload;
			})
			.addCase(fetchLiveStreamHistory.rejected, state => {
				state.isFetchingHistory = false;
			})
			.addCase(uploadAttachment.fulfilled, (state, action) => {
				state.mainImageKey = action.payload;
			})
			.addCase(fetchLiveStream.pending, state => {
				state.isFetchingStream = true;
			})
			.addCase(fetchLiveStream.fulfilled, (state, action) => {
				state.isFetchingStream = false;
				state.selectedLiveStream = action.payload;
			})
			.addCase(fetchLiveStream.rejected, state => {
				state.isFetchingStream = false;
			})
			.addCase(fetchLiveStreamViewersCount.fulfilled, (state, action) => {
				state.selectedLiveStreamViewersCount = action.payload.totalWebRTCWatchersCount;
			})
			.addCase(fetchLiveStreamComments.pending, state => {
				state.isFetchingComments = true;
			})
			.addCase(fetchLiveStreamComments.fulfilled, (state, action) => {
				state.streamComments = action.payload;
				state.isFetchingComments = false;
			})
			.addCase(fetchLiveStreamComments.rejected, state => {
				state.isFetchingComments = false;
			})
			.addCase(deleteLiveStreamComment.pending, (state, action) => {
				const { commentId } = action.meta.arg;
				if (!state.streamComments) return;
				state.streamComments.items = state.streamComments?.items?.map(comment => {
					if (+comment.id === +commentId) {
						return { ...comment, isDeleting: true };
					}
					return comment;
				});
			})
			.addCase(deleteLiveStreamComment.fulfilled, (state, action) => {
				const { commentId } = action.meta.arg;
				if (!state.streamComments) return;
				state.streamComments.items = state.streamComments?.items?.map(comment => {
					if (+comment.id === +commentId) {
						return { ...comment, isDeleting: false };
					}
					return comment;
				});
			})
			.addCase(deleteLiveStreamComment.rejected, (state, action) => {
				const { commentId } = action.meta.arg;
				if (!state.streamComments) return;
				state.streamComments.items = state.streamComments?.items?.map(comment => {
					if (+comment.id === +commentId) {
						return { ...comment, isDeleting: false };
					}
					return comment;
				});
			})
});

export const getLiveStreamFullState = (state: RootState): LiveStreamState => state.liveStream;
export const { AddNewComment, clearSelectedStream } = liveStreamSlice.actions;
export default liveStreamSlice.reducer;
